import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import FloatingMenu from 'shared/components/ui/FloatingMenu';
import { dataTableFocusRing } from 'features/playground/styles';

/**
 * BrandMultiSelect — on-brand multiselect dropdown, the multi-value sibling of
 * BrandSelect. Selections show in the trigger as removable chips on ONE line
 * (the first few, then "+N"), so picking many never makes the trigger, and the
 * card around it, grow.
 *
 * The menu is a FloatingMenu: portaled out of any scrolling ancestor, kept
 * inside the viewport, opening above or below per the surrounding
 * MenuPlacementProvider, scrolling its own option list. A search box appears
 * once there are 8+ options; a footer counts the selection and clears it.
 *
 * Keyboard: ArrowDown opens from the trigger; in the menu Arrow keys move,
 * Enter / Space toggle, Home / End jump, Escape closes (and only the menu).
 *
 *   options: [{ value, label, icon?: ReactNode, hint?: string }]
 *   value:   array of selected values
 *   onChange(nextArray)
 */
const SEARCH_AT = 8;

export default function BrandMultiSelect({
  value = [],
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  minWidth = 160,
  menuMinWidth = 220,
  maxChips = 2,
  emptyLabel = 'No options',
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [menuWidth, setMenuWidth] = useState(menuMinWidth);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const uid = useId();

  const selectedSet = useMemo(() => new Set((value || []).map(String)), [value]);
  const selectedOpts = useMemo(
    () => options.filter((o) => selectedSet.has(String(o.value))),
    [options, selectedSet],
  );
  const searchable = options.length >= SEARCH_AT;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label ?? o.value).toLowerCase().includes(q) || String(o.hint || '').toLowerCase().includes(q));
  }, [options, query]);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  const openMenu = () => {
    if (disabled) return;
    setMenuWidth(Math.max(wrapRef.current?.offsetWidth || 0, menuMinWidth));
    // Start on the first selected row so a reopened menu shows what is chosen.
    const firstOn = options.findIndex((o) => selectedSet.has(String(o.value)));
    setActive(Math.max(0, firstOn));
    setOpen(true);
  };

  // Focus lands in the menu so the keyboard can drive it.
  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => (searchable ? searchRef.current : listRef.current)?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(t);
  }, [open, searchable]);

  // Keep the active row in view as the arrows move.
  useEffect(() => {
    if (!open) return;
    document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, uid]);

  const toggle = (v) => {
    const key = String(v);
    onChange(selectedSet.has(key) ? (value || []).filter((x) => String(x) !== key) : [...(value || []), v]);
  };
  const remove = (v, e) => {
    e.stopPropagation();
    onChange((value || []).filter((x) => String(x) !== String(v)));
  };

  const onMenuKey = (e) => {
    const typing = e.target === searchRef.current;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(shown.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home' && !typing) { e.preventDefault(); setActive(0); }
    else if (e.key === 'End' && !typing) { e.preventDefault(); setActive(Math.max(0, shown.length - 1)); }
    else if (e.key === 'Enter' || (e.key === ' ' && !typing)) {
      e.preventDefault();
      if (shown[active]) toggle(shown[active].value);
    } else if (e.key === 'Tab') close();
  };

  const visible = selectedOpts.slice(0, maxChips);
  const extra = selectedOpts.length - visible.length;

  return (
    <div ref={wrapRef} className="relative" style={{ minWidth }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); openMenu(); } }}
        className={`flex h-8 w-full items-center gap-1.5 rounded-md border bg-surface px-2 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-50 ${open ? 'border-[color:var(--border-strong)]' : 'border-[color:var(--border-default)]'} ${dataTableFocusRing}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
          {selectedOpts.length === 0 && (
            <span className="truncate text-[color:var(--text-disabled)]">{placeholder}</span>
          )}
          {visible.map((o) => (
            <span key={String(o.value)} className="inline-flex min-w-0 max-w-[150px] shrink items-center gap-1 rounded-md border border-[color:var(--border-default)] px-1.5 py-0.5 text-[11px] text-[color:var(--text-primary)]">
              {o.icon}
              <span className="truncate">{o.label}</span>
              {!disabled && (
                <span role="button" tabIndex={-1} aria-label={`Remove ${o.label}`} title={`Remove ${o.label}`} onClick={(e) => remove(o.value, e)} className="shrink-0 rounded text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
                  <X className="h-3 w-3" />
                </span>
              )}
            </span>
          ))}
          {extra > 0 && (
            <span className="shrink-0 rounded-md bg-surface-hover px-1.5 py-0.5 text-[11px] font-medium text-[color:var(--text-secondary)]" title={selectedOpts.slice(maxChips).map((o) => o.label).join(', ')}>+{extra}</span>
          )}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <FloatingMenu anchorRef={wrapRef} open={open} onClose={close} width={menuWidth} scroll={false} maxHeight={320} onKeyDown={onMenuKey}>
        {searchable && (
          <div className="shrink-0 border-b border-[color:var(--border-subtle)] p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                placeholder="Search…"
                aria-label="Search options"
                aria-controls={`${uid}-list`}
                aria-activedescendant={shown[active] ? `${uid}-opt-${active}` : undefined}
                className="h-7 w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] pl-7 pr-2 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)]"
              />
            </div>
          </div>
        )}
        <div
          ref={listRef}
          id={`${uid}-list`}
          role="listbox"
          aria-label={ariaLabel || placeholder}
          aria-multiselectable="true"
          aria-activedescendant={shown[active] ? `${uid}-opt-${active}` : undefined}
          tabIndex={searchable ? -1 : 0}
          className="min-h-0 flex-1 overflow-y-auto py-1 outline-none"
        >
          {shown.length === 0 && (
            <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">{options.length === 0 ? emptyLabel : 'No matches.'}</div>
          )}
          {shown.map((opt, i) => {
            const isSel = selectedSet.has(String(opt.value));
            return (
              <div
                key={String(opt.value)}
                id={`${uid}-opt-${i}`}
                role="option"
                aria-selected={isSel}
                onClick={() => toggle(opt.value)}
                onMouseMove={() => { if (active !== i) setActive(i); }}
                className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[12px] text-[color:var(--text-primary)] transition-colors ${i === active ? 'bg-surface-hover' : ''}`}
              >
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${isSel ? 'border-[color:var(--trodo-primary)] bg-[color:var(--trodo-primary)] text-ink-onBrand' : 'border-[color:var(--border-strong)] text-transparent'}`}>
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {opt.icon}
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                {opt.hint && <span className="shrink-0 text-[10.5px] text-[color:var(--text-muted)]">{opt.hint}</span>}
              </div>
            );
          })}
        </div>
        {options.length > 0 && (
          <div className="flex shrink-0 items-center justify-between border-t border-[color:var(--border-subtle)] px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]">
            <span aria-live="polite">{selectedOpts.length ? `${selectedOpts.length} selected` : 'None selected'}</span>
            <button type="button" onClick={() => onChange([])} disabled={!selectedOpts.length} className="font-medium text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] disabled:cursor-default disabled:opacity-40">Clear</button>
          </div>
        )}
      </FloatingMenu>
    </div>
  );
}
